import posthog from 'posthog-js'

import { ChainId } from '@src/configs/chainInfo'
import { IToken } from '@src/types/token'
import { computeUSDAmount } from '@src/utils/balance'
import { createContextLogger } from '@src/utils/logger'
import { useTokensStore } from '@src/zustand_stores/tokens.store'

const logger = createContextLogger('AnalyticsService')
/**
 * Analytics service for tracking user behavior
 * Note: PostHog must be initialized in App.tsx before using these methods
 */
class AnalyticsService {
    /**
     * Track a user action with additional properties
     * @param eventName The name of the event
     * @param properties Additional properties to track
     */
    public trackEvent(eventName: string, properties: Record<string, any> = {}): void {
        try {
            // Track the event directly with PostHog
            posthog.capture(eventName, properties)
            logger.debug(`Tracked event: ${eventName}`, properties)
        } catch (error) {
            logger.error(`Error tracking event: ${eventName}`, error)
        }
    }

    /**
     * Calculate the total USD value of a wallet based on token balances
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @returns The total wallet value in USD
     */
    private calculateWalletValue(address: string, chainType: string): number {
        try {
            // Get tokens from the tokens store
            const tokensStore = useTokensStore.getState()
            const tokens = tokensStore.tokens

            // Filter tokens for the given wallet address and chain
            const walletTokens = tokens.filter((token: IToken) => {
                // For EVM chains, we need to check if the token belongs to the correct chain
                if (chainType === 'EVM') {
                    return token.userBalance !== undefined && token.userBalance > BigInt(0)
                }
                // For Solana, we need to check if the token belongs to the Solana chain
                else if (chainType === 'SVM') {
                    return (
                        token.chainId === ChainId.Solana &&
                        token.userBalance !== undefined &&
                        token.userBalance > BigInt(0)
                    )
                }
                // For Tron, we need to check if the token belongs to the Tron chain
                else if (chainType === 'TRX') {
                    return (
                        token.chainId === ChainId.Tron &&
                        token.userBalance !== undefined &&
                        token.userBalance > BigInt(0)
                    )
                }
                return false
            })

            // Calculate total wallet value in USD
            let totalWalletValueUSD = 0

            walletTokens.forEach((token: IToken) => {
                if (token.userBalance && token.price_current) {
                    const tokenValueUSD = computeUSDAmount(token, token.userBalance)
                    if (tokenValueUSD !== undefined) {
                        totalWalletValueUSD += tokenValueUSD
                    }
                }
            })

            logger.debug('Calculated wallet value:', {
                address,
                chainType,
                totalWalletValueUSD,
                tokenCount: walletTokens.length,
            })

            return totalWalletValueUSD
        } catch (error) {
            logger.error('Error calculating wallet value:', error)
            return 0
        }
    }

    /**
     * Update the wallet value in PostHog after a transaction
     * This should be called after a transaction completes to keep wallet value metrics up-to-date
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @param transactionValueUSD Optional USD value of the transaction that triggered this update
     */
    public updateWalletValueAfterTransaction(address: string, chainType: string, transactionValueUSD?: number): void {
        try {
            // Calculate the updated wallet value
            const walletValueUSD = this.calculateWalletValue(address, chainType)

            // Update user properties with the new wallet value
            posthog.people.set({
                wallet_value_usd: walletValueUSD,
                last_wallet_value_update: new Date().toISOString(),
                wallet_value_updated_by: 'transaction',
            })

            // Also track a separate event for wallet value updates
            this.trackEvent('wallet_value_updated', {
                wallet_address: address,
                chain_type: chainType,
                wallet_value_usd: walletValueUSD,
                update_source: 'transaction',
                timestamp: new Date().toISOString(),
            })

            // If transaction value is provided, update the user's contribution metrics
            if (transactionValueUSD !== undefined && transactionValueUSD > 0) {
                this.updateUserContribution(address, transactionValueUSD)
            }

            logger.debug('Updated wallet value after transaction:', {
                address,
                chainType,
                walletValueUSD,
                transactionValueUSD,
            })
        } catch (error) {
            logger.error('Error updating wallet value after transaction:', error)
        }
    }

    /**
     * Update the user's contribution metrics in PostHog
     * This tracks the total value contributed by the user through transactions
     * @param address The wallet address
     * @param transactionValueUSD The USD value of the current transaction
     */
    private updateUserContribution(address: string, transactionValueUSD: number): void {
        try {
            // Get current contribution values from PostHog
            const currentProperties = posthog.get_property('contribution_total_usd') || 0
            const transactionCount = posthog.get_property('contribution_transaction_count') || 0

            // Calculate new values
            const newTotalContribution = Number(currentProperties) + transactionValueUSD
            const newTransactionCount = Number(transactionCount) + 1
            const averageContribution = newTransactionCount > 0 ? newTotalContribution / newTransactionCount : 0

            // Update user properties with contribution metrics
            posthog.people.set({
                contribution_total_usd: newTotalContribution,
                contribution_transaction_count: newTransactionCount,
                contribution_average_usd: averageContribution,
                last_contribution_value_usd: transactionValueUSD,
                last_contribution_date: new Date().toISOString(),
            })

            // Also track incremental values for accurate counting
            // Note: Using direct property updates since increment may not be available in all PostHog versions
            this.trackEvent('contribution_increment', {
                contribution_total_usd: transactionValueUSD,
                contribution_transaction_count: 1,
            })

            // Track a separate event for contribution updates
            this.trackEvent('user_contribution_updated', {
                wallet_address: address,
                transaction_value_usd: transactionValueUSD,
                total_contribution_usd: newTotalContribution,
                transaction_count: newTransactionCount,
                average_contribution_usd: averageContribution,
                timestamp: new Date().toISOString(),
            })

            logger.debug('Updated user contribution metrics:', {
                address,
                transactionValueUSD,
                newTotalContribution,
                newTransactionCount,
                averageContribution,
            })
        } catch (error) {
            logger.error('Error updating user contribution metrics:', error)
        }
    }

    /**
     * Track when a user connects their wallet
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     */
    public trackWalletConnect(address: string, chainType: string, walletName?: string): void {
        try {
            // Get the current distinct ID (anonymous user ID)
            const distinctId = posthog.get_distinct_id()

            // Create a wallet-specific ID to use as an alias
            const walletAliasId = `${chainType}_${address}`

            // Store the wallet address in localStorage to track connected wallets
            this.storeConnectedWallet(address, chainType)

            try {
                // Create an alias between the anonymous ID and the wallet address
                posthog.alias(walletAliasId, distinctId)

                logger.info('Created alias for wallet', {
                    distinctId,
                    walletAliasId,
                    chainType,
                })
            } catch (error) {
                // If alias creation fails (e.g., if the alias was already used), log the error
                logger.error('Error creating alias for wallet:', error)
            }

            // Calculate wallet value
            const walletValueUSD = this.calculateWalletValue(address, chainType)

            // Track the wallet connection event with wallet value
            this.trackEvent('wallet_connected', {
                wallet_address: address,
                chain_type: chainType,
                wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                wallet_alias_id: walletAliasId,
                wallet_value_usd: walletValueUSD,
            })

            // Update user properties with wallet information including wallet value
            posthog.people.set({
                wallet_address: address,
                chain_type: chainType,
                wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                has_connected_wallet: true,
                connected_at: new Date().toISOString(),
                wallet_value_usd: walletValueUSD,
                last_wallet_value_update: new Date().toISOString(),
            })

            logger.info('Wallet connected with value tracking:', {
                address,
                chainType,
                walletName: walletName || this.getWalletNameFromChainType(chainType),
                walletValueUSD,
            })
        } catch (error) {
            logger.error('Error tracking wallet connect:', error)
        }
    }

    /**
     * Helper method to get a default wallet name from chain type
     * @param chainType The type of chain (EVM, SVM, TRX, etc.)
     * @returns A default wallet name for the chain type
     */
    private getWalletNameFromChainType(chainType: string): string {
        switch (chainType) {
            case 'EVM':
                return 'MetaMask'
            case 'SVM':
                return 'Phantom'
            case 'TRX':
                return 'TronLink'
            default:
                return chainType
        }
    }

    /**
     * Track when a user disconnects their wallet
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    public trackWalletDisconnect(address: string, chainType: string): void {
        try {
            this.trackEvent('wallet_disconnected', {
                wallet_address: address,
                chain_type: chainType,
                disconnected_at: new Date().toISOString(),
            })
        } catch (error) {
            logger.error('Error tracking wallet disconnect:', error)
        }
    }

    /**
     * Track when a specific wallet connection is attempted
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    public trackSpecificWalletConnectionAttempt(walletName: string, chainType: string): void {
        try {
            this.trackEvent('wallet_connection_attempt', {
                wallet_name: walletName,
                chain_type: chainType,
                attempted_at: new Date().toISOString(),
            })
        } catch (error) {
            logger.error('Error tracking wallet connection attempt:', error)
        }
    }

    /**
     * Track when a wallet connection attempt fails
     * @param error The error that occurred
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    public trackWalletConnectionFailed(error: any, walletName: string, chainType: string): void {
        try {
            this.trackEvent('wallet_connection_failed', {
                wallet_name: walletName,
                chain_type: chainType,
                error_message: error?.message || String(error),
                error_code: error?.code,
                failed_at: new Date().toISOString(),
            })
        } catch (trackError) {
            logger.error('Error tracking wallet connection failure:', trackError)
        }
    }

    /**
     * Track feature usage
     * @param featureName The name of the feature
     * @param properties Additional properties
     */
    public trackFeatureUsage(featureName: string, properties: Record<string, any> = {}): void {
        try {
            this.trackEvent('feature_used', {
                feature_name: featureName,
                ...properties,
                used_at: new Date().toISOString(),
            })
        } catch (error) {
            logger.error('Error tracking feature usage:', error)
        }
    }

    /**
     * Store connected wallet information in localStorage
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    private storeConnectedWallet(address: string, chainType: string): void {
        try {
            // Get existing connected wallets or initialize empty array
            const connectedWalletsString = localStorage.getItem('mach_connected_wallets') || '[]'
            const connectedWallets = JSON.parse(connectedWalletsString)

            // Check if this wallet is already stored
            const walletExists = connectedWallets.some(
                (wallet: { address: string; chainType: string }) =>
                    wallet.address === address && wallet.chainType === chainType
            )

            // If wallet doesn't exist, add it to the array
            if (!walletExists) {
                connectedWallets.push({
                    address,
                    chainType,
                    connectedAt: new Date().toISOString(),
                })

                // Store updated array back in localStorage
                localStorage.setItem('mach_connected_wallets', JSON.stringify(connectedWallets))
            }
        } catch (error) {
            logger.error('Error storing connected wallet:', error)
        }
    }

    /**
     * Get all connected wallets from localStorage
     * @returns Array of connected wallet objects
     */
    public getConnectedWallets(): Array<{ address: string; chainType: string; connectedAt: string }> {
        try {
            const connectedWalletsString = localStorage.getItem('mach_connected_wallets') || '[]'
            return JSON.parse(connectedWalletsString)
        } catch (error) {
            logger.error('Error getting connected wallets:', error)
            return []
        }
    }

    /**
     * Manually capture an exception for error tracking
     * @param error The error object to capture
     * @param additionalProperties Additional properties to include with the exception
     */
    public captureException(error: Error | unknown, additionalProperties: Record<string, any> = {}): void {
        try {
            // Create properties object for the exception
            const properties = {
                ...additionalProperties,
                timestamp: new Date().toISOString(),
            }

            // Use PostHog's captureException method
            posthog.captureException(error, properties)

            // Also log to console in development
            if (process.env.NODE_ENV !== 'production') {
                logger.error('Exception captured:', error, properties)
            }
        } catch (captureError) {
            // Fallback if captureException fails
            logger.error('Error capturing exception:', captureError)

            // Try to capture as a regular event
            this.trackEvent('error', {
                error_message: error instanceof Error ? error.message : String(error),
                error_name: error instanceof Error ? error.name : 'Unknown',
                error_stack: error instanceof Error ? error.stack : undefined,
                ...additionalProperties,
            })
        }
    }
}

// Export a singleton instance
export const analyticsService = new AnalyticsService()
